<template>
  <div>
    <b-row>
      <b-col cols="12">
        <div class="d-flex justify-content-between align-items-center mb-2">
          <h2 class="mb-0">
            Selection Record
          </h2>
          <div class="d-flex gap-1">
            <b-button
              variant="outline-secondary"
              size="sm"
              class="d-flex align-items-center"
              :disabled="!savedRecordId"
              @click="downloadPDF"
            >
              <b-spinner
                v-if="!savedRecordId"
                small
                style="width:20px;height:20px;"
              />
              <b-img
                v-else
                :src="require('@/assets/images/icons/file-icons/adobe_pdf_icon_modern.png')"
                height="20"
                style="width: auto; position: relative; top: -2px;"
              />
              <span class="ml-50 font-weight-bold text-body">View as PDF</span>
            </b-button>
            <b-button
              variant="secondary"
              @click="$router.back()"
            >
              Back to Edit
            </b-button>
          </div>
        </div>

        <p
          v-if="savedRecordId && !error"
          class="lead mb-2"
        >
          Your selection record document is available as a PDF and is permanently saved in
          <router-link :to="{ name: 'selection-records' }">Selection Records</router-link>.
        </p>

        <div v-if="loading">
          <loading-container />
        </div>

        <no-data-container
          v-else-if="error"
          :message="errorMessage || 'No record data found'"
        />

        <b-card v-else-if="recordData" class="generated-record-card">
          <!-- Record Header -->
          <div class="record-header mb-0">
            <h3 class="mb-50">
              {{ recordData.recordName || 'Untitled Selection Record' }}
            </h3>
            <p class="mb-0 font-weight-bold">
              Generated<template v-if="usersFullName">
                for {{ usersFullName }}<template v-if="companyName">
                  of {{ companyName }}
                </template>
              </template> on {{ generatedDateTime }}
            </p>
          </div>

          <hr>

          <!-- Contextual summary paragraphs -->
          <div class="section mb-0">
            <p>There were a total of <strong>{{ recordData.totalProductCount || 0 }}</strong> available products listed on the FVC website StructuredEdge at the time when the report was generated.</p>
            <p v-if="filterCount >= 2">A total of <strong>{{ filterCount }}</strong> filter(s) were selected in the order shown below.</p>

            <b-table
              v-if="filterTableRows.length > 0"
              :items="filterTableRows"
              :fields="filterTableFields"
              small
              bordered
              class="mb-2"
            >
              <template #cell(index)="data">
                {{ data.item.index }}
              </template>
              <template #cell(label)="data">
                {{ data.item.label }}
              </template>
              <template #cell(selectedValues)="data">
                <b-badge
                  v-for="(value, i) in data.item.selectedValues"
                  :key="i"
                  variant="light-primary"
                  class="mr-50 mb-25"
                  pill
                >
                  {{ value }}
                </b-badge>
              </template>
              <template #cell(reason)="data">
                <em>{{ data.item.reason }}</em>
              </template>
              <template #cell(productsDiscarded)="data">
                {{ data.item.productsDiscarded !== null ? data.item.productsDiscarded : '—' }}
              </template>
              <template #cell(productsRemaining)="data">
                {{ data.item.productsRemaining !== null ? data.item.productsRemaining : '—' }}
              </template>
            </b-table>
            <p>From the filtered product selection you have identified <strong>{{ recordData.reportIds ? recordData.reportIds.length : 0 }}</strong> product(s) as being suitable.</p>
            <div class="mt-1">
              <span class="font-weight-bold">Selection Reason:</span>
              <p class="mb-0 mt-25">
                {{ recordData.tableReason || 'No reason given' }}
              </p>
            </div>
          </div>

          <!-- Date Constraints -->
          <div v-if="hasActiveConstraints" class="section mb-3">
            <h5 class="font-weight-bold mb-2">
              Date Constraints
            </h5>
            <div class="mb-1">
              <b-badge variant="light-info" pill>
                {{ constraintSummary }}
              </b-badge>
            </div>
            <p v-if="recordData.constraintReason" class="mb-0 ml-1">
              <em>{{ recordData.constraintReason }}</em>
            </p>
          </div>
        </b-card>

        <!-- Product Summary Title -->
        <h4 v-if="!error && !loading && recordData" class="mt-2 mb-1">
          Product Summary
        </h4>

        <!-- Selected Products (Collapsible) -->
        <b-card
          v-if="!error && !loading && recordData"
          no-body
          class="selected-products-collapse mt-2"
        >
          <custom-collapsable
:title="`${recordData.products ? recordData.products.length : 0} Products Selected`"
            :is-visible="false"
          >
            <b-table
              responsive
              :items="recordData.products"
              :fields="productTableFields"
            >
              <template #head(provider)>
                <div>Provider</div>
                <div>Product</div>
                <div>Counterparty</div>
              </template>
              <template #head(type)>
                <div>Type</div>
                <div>Underlying</div>
                <div>Length</div>
              </template>
              <template #head(capital)>
                <div>Capital</div>
                <div>Aim</div>
                <div>Vehicle</div>
              </template>
              <template #cell(reportNumber)="data">
                <router-link :to="{ name: 'product-report', params: { reportNumber: data.item.reportNumber } }" class="text-body">
                  {{ data.item.reportNumber }}
                </router-link>
              </template>
              <template #cell(closingDate)="data">
                {{ formatIsoDate(data.item.closingDate) }}
              </template>
              <template #cell(provider)="data">
                <div class="d-flex flex-column">
                  <span>{{ data.item.provider }}</span>
                  <span>{{ data.item.productName }}</span>
                  <span>{{ data.item.counterParty }}</span>
                </div>
              </template>
              <template #cell(type)="data">
                <div class="d-flex flex-column">
                  <span>{{ data.item.type }}</span>
                  <span>{{ data.item.underlying }}</span>
                  <span>{{ data.item.length }}</span>
                </div>
              </template>
              <template #cell(capital)="data">
                <div class="d-flex flex-column">
                  <span>{{ data.item.capital }}</span>
                  <span>{{ data.item.aim }}</span>
                  <span>{{ data.item.vehicle }}</span>
                </div>
              </template>
            </b-table>
          </custom-collapsable>
        </b-card>

        <!-- Non-Selected Filter Matches (Collapsible) -->
        <b-card
          v-if="!error && !loading && recordData && excludedProducts.length > 0"
          no-body
          class="mt-2"
        >
          <custom-collapsable
            :title="`${excludedProducts.length} Products Not Selected`"
            :is-visible="false"
          >
            <b-table
              responsive
              :items="excludedProducts"
              :fields="productTableFields"
            >
              <template #head(provider)>
                <div>Provider</div>
                <div>Product</div>
                <div>Counterparty</div>
              </template>
              <template #head(type)>
                <div>Type</div>
                <div>Underlying</div>
                <div>Length</div>
              </template>
              <template #head(capital)>
                <div>Capital</div>
                <div>Aim</div>
                <div>Vehicle</div>
              </template>
              <template #cell(reportNumber)="data">
                <router-link :to="{ name: 'product-report', params: { reportNumber: data.item.reportNumber } }" class="text-body">
                  {{ data.item.reportNumber }}
                </router-link>
              </template>
              <template #cell(closingDate)="data">
                {{ formatIsoDate(data.item.closingDate) }}
              </template>
              <template #cell(provider)="data">
                <div class="d-flex flex-column">
                  <span>{{ data.item.provider }}</span>
                  <span>{{ data.item.productName }}</span>
                  <span>{{ data.item.counterParty }}</span>
                </div>
              </template>
              <template #cell(type)="data">
                <div class="d-flex flex-column">
                  <span>{{ data.item.type }}</span>
                  <span>{{ data.item.underlying }}</span>
                  <span>{{ data.item.length }}</span>
                </div>
              </template>
              <template #cell(capital)="data">
                <div class="d-flex flex-column">
                  <span>{{ data.item.capital }}</span>
                  <span>{{ data.item.aim }}</span>
                  <span>{{ data.item.vehicle }}</span>
                </div>
              </template>
            </b-table>
          </custom-collapsable>
        </b-card>

        <!-- Failed Filter Matches (Collapsible) -->
        <b-card
          v-if="!error && !loading && recordData && failedFilterCount > 0"
          no-body
          class="mt-2"
        >
          <custom-collapsable
            :title="`${failedFilterCount} Products Omitted`"
            :is-visible="false"
          >
            <b-table
              responsive
              :items="omittedProducts"
              :fields="productTableFields"
            >
              <template #head(provider)>
                <div>Provider</div>
                <div>Product</div>
                <div>Counterparty</div>
              </template>
              <template #head(type)>
                <div>Type</div>
                <div>Underlying</div>
                <div>Length</div>
              </template>
              <template #head(capital)>
                <div>Capital</div>
                <div>Aim</div>
                <div>Vehicle</div>
              </template>
              <template #cell(reportNumber)="data">
                <router-link :to="{ name: 'product-report', params: { reportNumber: data.item.reportNumber } }" class="text-body">
                  {{ data.item.reportNumber }}
                </router-link>
              </template>
              <template #cell(closingDate)="data">
                {{ formatIsoDate(data.item.closingDate) }}
              </template>
              <template #cell(provider)="data">
                <div class="d-flex flex-column">
                  <span>{{ data.item.provider }}</span>
                  <span>{{ data.item.productName }}</span>
                  <span>{{ data.item.counterParty }}</span>
                </div>
              </template>
              <template #cell(type)="data">
                <div class="d-flex flex-column">
                  <span>{{ data.item.type }}</span>
                  <span>{{ data.item.underlying }}</span>
                  <span>{{ data.item.length }}</span>
                </div>
              </template>
              <template #cell(capital)="data">
                <div class="d-flex flex-column">
                  <span>{{ data.item.capital }}</span>
                  <span>{{ data.item.aim }}</span>
                  <span>{{ data.item.vehicle }}</span>
                </div>
              </template>
            </b-table>
          </custom-collapsable>
        </b-card>

        <!-- Preference Analysis -->
        <preference-analysis
          v-if="!error && !loading && recordData"
          ref="preferenceAnalysis"
          :preference-info="preferenceDropdown"
          :suitability-info="suitabilityInfo"
          :selected-preference-id="selectedPreferenceId"
          :hide-preference-cards="false"
          mode="multi"
          @preference-changed="onPreferenceChanged"
          @charts-ready="chartsReady = true"
        />
      </b-col>
    </b-row>
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BButton, BBadge, BImg, BSpinner, BTable,
} from 'bootstrap-vue'
import { mapGetters } from 'vuex'
import {
  formatIsoDate,
  formatNumberTextMaxDecimalPlaces,
} from '@/utils/formatters'
import LoadingContainer from '@/views/components/LoadingContainer.vue'
import NoDataContainer from '@/views/components/NoDataContainer.vue'
import PreferenceAnalysis from '@/views/products/breakdown/PreferenceAnalysis.vue'
import CustomCollapsable from '@/views/components/CustomCollapsable.vue'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BButton,
    BBadge,
    BImg,
    BSpinner,
    BTable,
    LoadingContainer,
    NoDataContainer,
    PreferenceAnalysis,
    CustomCollapsable,
  },
  data() {
    return {
      recordData: null,
      loading: false,
      error: false,
      errorMessage: '',
      preferenceDropdown: [],
      suitabilityInfo: null,
      selectedPreferenceId: null,
      chartsReady: false,
      savedRecordId: null,
      omittedProducts: [],
      baseURL: process.env.VUE_APP_BACKEND_PATH,
      productTableFields: [
        { key: 'reportNumber', label: 'Report Number', sortable: true },
        { key: 'closingDate', label: 'Closing Date', sortable: true },
        { key: 'provider', label: 'Provider', sortable: true },
        { key: 'type', label: 'Type', sortable: true },
        { key: 'capital', label: 'Capital', sortable: true },
        { key: 'score', label: 'Score', sortable: true },
        { key: 'risk', label: 'Risk', sortable: true },
      ],
      filterTableFields: [
        { key: 'index', label: '#', thStyle: { width: '3%' } },
        { key: 'label', label: 'Filter Choice', thStyle: { width: '15%' } },
        { key: 'selectedValues', label: 'Selection', thStyle: { width: '20%' } },
        { key: 'reason', label: 'Reason', thStyle: { width: '30%' } },
        { key: 'productsDiscarded', label: 'Products Discarded', thStyle: { width: '16%' } },
        { key: 'productsRemaining', label: 'Products Remaining', thStyle: { width: '16%' } },
      ],
    }
  },
  computed: {
    ...mapGetters('auth', ['usersFullName']),
    companyName() {
      return this.$store.state.auth.user.companyname
    },
    excludedProducts() {
      return (this.recordData && this.recordData.excludedProducts) ? this.recordData.excludedProducts : []
    },
    failedFilterCount() {
      return this.omittedProducts.length
    },
    filterCount() {
      return this.activeFilters.length
    },
    selectedCount() {
      const fallback = this.recordData && this.recordData.reportIds ? this.recordData.reportIds.length : 0
      if (!this.filterTableRows.length) return fallback
      const last = this.filterTableRows[this.filterTableRows.length - 1]
      if (last.productsRemaining !== null) return last.productsRemaining
      return fallback
    },
    selectedReportNumbersText() {
      if (!this.recordData || !this.recordData.reportIds) return ''
      return this.recordData.reportIds.map(id => `${id}`).join(', ')
    },
    generatedDateTime() {
      const now = new Date()
      const date = now.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
      const time = now.toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      })
      return `${date} at ${time}`
    },
    currentDate() {
      const by = this.usersFullName ? ` by ${this.usersFullName}` : ''
      return `${this.generatedDateTime}${by}`
    },
    activeFilters() {
      if (!this.recordData || !this.recordData.filterData || !this.recordData.filterDisplay) {
        return []
      }

      const filters = []

      if (this.recordData.search) {
        filters.push({ key: 'search', label: 'Search', selectedValues: [this.recordData.search] })
      }

      Object.keys(this.recordData.filterData).forEach(categoryKey => {
        const selectedValues = this.recordData.filterData[categoryKey]

        if (!selectedValues || selectedValues.length === 0) {
          return
        }

        const category = this.recordData.filterDisplay.find(cat => cat.categoryKey === categoryKey)

        if (category) {
          // Check if it's a slider by checking if it has a 'slider' property
          const isSlider = category.slider !== undefined
          if (isSlider) {
            // For sliders, show as a range from min to max
            // Sort the selected values by their position in category.items
            const sortedItems = selectedValues
              .map(v => category.items.find(i => String(i.value) === String(v)))
              .filter(item => item) // Remove any nulls
              .sort((a, b) => {
                const indexA = category.items.indexOf(a)
                const indexB = category.items.indexOf(b)
                return indexA - indexB
              })

            if (sortedItems.length > 0) {
              const minValue = formatNumberTextMaxDecimalPlaces(sortedItems[0].label)
              const maxValue = formatNumberTextMaxDecimalPlaces(sortedItems[sortedItems.length - 1].label)
              const rangeDisplay = minValue === maxValue ? [minValue] : [`${minValue} - ${maxValue}`]

              filters.push({
                key: categoryKey,
                label: category.categoryLabel,
                selectedValues: rangeDisplay,
              })
            }
          } else {
            // For non-sliders, show all selected values
            const valueLabels = selectedValues.map(value => {
              const item = category.items.find(i => String(i.value) === String(value))
              return item ? item.label : value
            })

            filters.push({
              key: categoryKey,
              label: category.categoryLabel,
              selectedValues: valueLabels,
            })
          }
        }
      })

      return filters
    },
    filterTableRows() {
      if (!this.activeFilters.length) return []
      const sequentialCounts = this.recordData.filterSequentialCounts || {}
      let remaining = this.recordData.totalProductCount || 0
      return this.activeFilters.map((filter, index) => {
        const productsRemaining = sequentialCounts[filter.key] !== undefined
          ? sequentialCounts[filter.key]
          : null
        const productsDiscarded = productsRemaining !== null
          ? Math.max(0, remaining - productsRemaining)
          : null
        remaining = productsRemaining !== null ? productsRemaining : remaining
        return {
          index: index + 1,
          label: filter.label,
          selectedValues: filter.selectedValues,
          reason: this.recordData.filterReasons && this.recordData.filterReasons[filter.key]
            ? this.recordData.filterReasons[filter.key]
            : '—',
          productsDiscarded,
          productsRemaining,
        }
      })
    },
    hasActiveConstraints() {
      if (!this.recordData || !this.recordData.constraints) return false

      return (
        this.recordData.constraints.dateType
        && this.recordData.constraints.dateType !== 'None'
        && this.recordData.constraints.dateType !== 'none'
      )
    },
    constraintSummary() {
      if (!this.hasActiveConstraints) return ''

      const parts = []
      const typeLabels = {
        closingdate: 'Closing Date',
        strikedate: 'Strike Date',
        finalmarketdate: 'Final Market Date',
        Live: 'Live',
        Matured: 'Matured',
      }

      const dateType = typeLabels[this.recordData.constraints.dateType] || this.recordData.constraints.dateType

      if (this.recordData.constraints.dateFrom) {
        parts.push(`From: ${this.recordData.constraints.dateFrom}`)
      }
      if (this.recordData.constraints.dateTo) {
        parts.push(`To: ${this.recordData.constraints.dateTo}`)
      }

      return `${dateType} - ${parts.join(', ')}`
    },
  },
  mounted() {
    this.loadRecordData()
  },
  activated() {
    // Reload data when navigating back to this page (for keep-alive cached components)
    // Reset suitability data first to ensure watchers trigger
    this.suitabilityInfo = null
    this.preferenceDropdown = []
    this.selectedPreferenceId = null
    this.omittedProducts = []
    this.loadRecordData()
  },
  watch: {
    recordData: {
      immediate: true,
      handler(newVal) {
        if (newVal && newVal.products && newVal.products.length > 0) {
          this.loadPreferenceDropdown()
        }
      },
    },
    selectedPreferenceId: {
      immediate: true,
      handler(newVal) {
        if (newVal && this.recordData && this.recordData.products) {
          this.loadSuitabilityInfo()
        }
      },
    },
    chartsReady(newVal) {
      if (newVal) {
        this.saveSelectionRecord()
      }
    },
  },
  methods: {
    formatIsoDate,
    loadRecordData() {
      const stored = sessionStorage.getItem('generatedRecordData')

      if (stored) {
        try {
          this.recordData = JSON.parse(stored)
          this.loadOmittedProducts()
        } catch (e) {
          console.error('Failed to parse generated record data:', e)
          this.error = true
          this.errorMessage = 'Failed to load record data.'
        }
      } else {
        this.error = true
        this.errorMessage = 'No record data found. Please create a selection record first.'
      }
    },
    loadOmittedProducts() {
      const selectedIds = this.recordData.reportIds || []
      const excludedIds = (this.recordData.excludedReportIds || [])
      const allFilterIds = [...new Set([...selectedIds, ...excludedIds])]

      if (allFilterIds.length === 0) return

      this.$http.get('productFilter.cfc', {
        params: {
          method: 'getInverseFilterResults',
          reportNumbers: allFilterIds.join(','),
        },
      }).then(response => {
        this.omittedProducts = response.data.gridcontent || []
      }).catch(() => {})
    },
    loadPreferenceDropdown() {
      // Get the list of product IDs from recordData
      const productIds = this.recordData.products.map(p => p.reportNumber).join(',')

      this.$http
        .get('premium.cfc', {
          params: {
            method: 'getSuitabilityPreferenceDropdown',
            productIdIn: productIds,
            mode: 'multi',
          },
        })
        .then(response => {
          this.preferenceDropdown = response.data
          // Set the first preference as default
          if (this.preferenceDropdown && this.preferenceDropdown.length > 0) {
            this.selectedPreferenceId = this.preferenceDropdown[0].preferenceid
          }
        })
        .catch(error => {
          console.error('Error loading preference dropdown:', error)
        })
    },
    loadSuitabilityInfo() {
      this.chartsReady = false
      const productIds = this.recordData.products.map(p => p.reportNumber).join(',')

      this.$http
        .get('premium.cfc', {
          params: {
            method: 'getSuitabilityInfo',
            productIdIn: productIds,
            preferenceId: this.selectedPreferenceId,
            mode: 'multi',
            showQuotientLines: false,
          },
        })
        .then(response => {
          this.suitabilityInfo = response.data
        })
        .catch(error => {
          console.error('Error loading suitability info:', error)
        })
    },
    onPreferenceChanged(preferenceId) {
      this.selectedPreferenceId = preferenceId
    },
    buildPayload() {
      const charts = this.$refs.preferenceAnalysis
        ? this.$refs.preferenceAnalysis.collectCharts()
        : {}

      const filterReasonsWithFallback = Object.fromEntries(
        Object.keys(this.recordData.filterReasons || {}).map(key => [
          key,
          this.recordData.filterReasons[key] || 'No reason given',
        ]),
      )

      return {
        charts,
        recordData: {
          ...this.recordData,
          recordName: this.recordData.recordName || 'Untitled Selection Record',
          tableReason: this.recordData.tableReason || 'No reason given',
          filterReasons: filterReasonsWithFallback,
        },
        generatedOn: this.currentDate,
        filterTableRows: this.filterTableRows,
        failedFilterCount: this.failedFilterCount,
        selectedReportNumbersText: this.selectedReportNumbersText,
        suitabilityInfo: this.suitabilityInfo,
        preferenceDropdown: this.preferenceDropdown,
      }
    },
    saveSelectionRecord() {
      const payload = this.buildPayload()

      const formData = new FormData()
      formData.append('payload', JSON.stringify(payload))
      formData.append('recordName', this.recordData.recordName)

      this.$http.post('selectionrecord.cfc?method=saveSelectionRecord', formData)
        .then(response => {
          if (response.data && response.data.success) {
            this.savedRecordId = response.data.id
          } else {
            this.$toast.error('Failed to save selection record.')
          }
        })
        .catch(() => {
          this.$toast.error('Failed to save selection record.')
        })
    },
    downloadPDF() {
      window.open(`${this.baseURL}cfc/dynamicreport/selection/selectionpdf.cfm?selectionId=${this.savedRecordId}`, '_blank')
    },
  },
}
</script>

<style scoped>
.gap-1 {
  gap: 1rem;
}

.generated-record-card {
  box-shadow: 0 4px 24px 0 rgba(34, 41, 47, 0.1);
}

.record-header {
  padding-bottom: 1rem;
}

.section {
  padding: 0.5rem 0;
}

.products-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 1rem;
  margin-top: 1rem;
}

.product-card {
  padding: 1rem;
  border: 1px solid #e9ecef;
  border-radius: 0.5rem;
  transition: all 0.2s ease;
}

.product-card:hover {
  border-color: #1a73e8;
  box-shadow: 0 2px 8px rgba(26, 115, 232, 0.15);
  transform: translateY(-2px);
}

.product-details {
  margin-top: 0.5rem;
  line-height: 1.6;
}

.product-details > div {
  margin-bottom: 0.25rem;
}

/* Dark Mode Support */
.dark-layout .generated-record-card {
  box-shadow: 0 4px 24px 0 rgba(0, 0, 0, 0.3);
}

.dark-layout .product-card {
  border-color: #3b4253;
  background-color: #283046;
}

.dark-layout .product-card:hover {
  border-color: #1a73e8;
}

/* Responsive */
@media (max-width: 767px) {
  .products-grid {
    grid-template-columns: 1fr;
  }
}
</style>
